import { create } from "zustand";
import { persist } from "zustand/middleware";

type UploadKey = string;

export type UploadEntry =
  | File
  | (Partial<File> & {
      name: string;
      url?: string;
      certificateType?: string;
      idx?: number;
      [key: string]: unknown;
    });

type UploadCollection = Record<UploadKey, UploadEntry[]>;
type FilesUpdater = UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[]);

type SetFilesOptions = {
  mode?: "replace" | "merge";
  maxFiles?: number;
};

interface UploadStoreState {
  filesByKey: UploadCollection;
  // In-flight Azure uploads per field key (not persisted).
  uploadsInFlightByKey: Record<UploadKey, number>;
  setFilesForKey: (key: UploadKey, files: FilesUpdater, options?: SetFilesOptions) => void;
  pushFilesForKey: (key: UploadKey, files: UploadEntry[], options?: SetFilesOptions) => void;
  updateFilesForKey: (key: UploadKey, updater: (prev: UploadEntry[]) => UploadEntry[]) => void;
  removeFileAtIndex: (key: UploadKey, index: number) => void;
  clearFilesForKey: (key: UploadKey) => void;
  clearAll: () => void;
  getFilesForKey: (key: UploadKey) => UploadEntry[];
  beginUploadInFlight: (key: UploadKey) => void;
  endUploadInFlight: (key: UploadKey) => void;
  isUploadInFlight: (key: UploadKey) => boolean;
  hasAnyUploadInFlight: () => boolean;
}

const omitKey = (source: UploadCollection, key: UploadKey) => {
  const { [key]: _unused, ...rest } = source;
  return rest;
};

const clampEntries = (entries: UploadEntry[], maxFiles?: number) => {
  if (!maxFiles || maxFiles < 1) return entries;
  const start = Math.max(entries.length - maxFiles, 0);
  return entries.slice(start);
};

const buildEntrySignature = (entry: UploadEntry): string => {
  if (entry instanceof File) {
    return ["file", entry.name, entry.size, entry.type, entry.lastModified].join("|");
  }

  if (entry && typeof entry === "object") {
    const normalized = Object.keys(entry)
      .sort()
      .map((key) => `${key}:${String((entry as Record<string, unknown>)[key])}`)
      .join("|");
    return `object|${normalized}`;
  }

  return `unknown|${String(entry)}`;
};

const dedupeEntries = (entries: UploadEntry[]) => {
  const seen = new Set<string>();
  const result: UploadEntry[] = [];

  for (let idx = entries.length - 1; idx >= 0; idx -= 1) {
    const entry = entries[idx];
    const signature = buildEntrySignature(entry);
    if (seen.has(signature)) continue;
    seen.add(signature);
    result.push(entry);
  }

  return result.reverse();
};

const getPersistedFileName = (url: string) => {
  return url.split("/").pop() || "attachment";
};

const normalizePersistableEntry = (entry: unknown): Exclude<UploadEntry, File> | null => {
  if (entry instanceof File) return null;

  if (!entry || typeof entry !== "object") return null;

  const candidate = entry as Record<string, unknown>;
  const url =
    (typeof candidate.url === "string" && candidate.url.trim()) ||
    (typeof candidate.documentUrl === "string" && candidate.documentUrl.trim());
  if (!url) return null;

  const name =
    (typeof candidate.name === "string" && candidate.name.trim()) ||
    (typeof candidate.fileName === "string" && candidate.fileName.trim()) ||
    (typeof candidate.documentName === "string" && candidate.documentName.trim()) ||
    getPersistedFileName(url);

  return { ...candidate, name, url } as Exclude<UploadEntry, File>;
};

export const sanitizePersistedFiles = (filesByKey: unknown): UploadCollection => {
  if (!filesByKey || typeof filesByKey !== "object") return {};

  return Object.entries(filesByKey as Record<string, unknown>).reduce<UploadCollection>(
    (sanitized, [key, entries]) => {
      const validEntries = Array.isArray(entries)
        ? entries
            .map(normalizePersistableEntry)
            .filter((entry): entry is Exclude<UploadEntry, File> => entry !== null)
        : [];
      if (validEntries.length) sanitized[key] = validEntries;
      return sanitized;
    },
    {},
  );
};

const mergeEntries = (existing: UploadEntry[], incoming: UploadEntry[], maxFiles?: number) => {
  if (!incoming.length) return clampEntries(existing, maxFiles);
  const combined = [...existing, ...incoming];
  return clampEntries(dedupeEntries(combined), maxFiles);
};

const resolveEntries = (
  prev: UploadEntry[],
  files: FilesUpdater,
  options?: SetFilesOptions,
): UploadEntry[] => {
  const mode = options?.mode ?? "replace";
  const maxFiles = options?.maxFiles;
  const next = typeof files === "function" ? files(prev) : files;

  if (mode === "merge") {
    return mergeEntries(prev, next, maxFiles);
  }

  return clampEntries(next, maxFiles);
};

export const useUploadStore = create<UploadStoreState>()(
  persist(
    (set, get) => ({
      filesByKey: {},
      uploadsInFlightByKey: {},
      beginUploadInFlight: (key) =>
        set((state) => ({
          uploadsInFlightByKey: {
            ...state.uploadsInFlightByKey,
            [key]: (state.uploadsInFlightByKey[key] ?? 0) + 1,
          },
        })),
      endUploadInFlight: (key) =>
        set((state) => {
          const nextCount = (state.uploadsInFlightByKey[key] ?? 0) - 1;
          if (nextCount <= 0) {
            const { [key]: _removed, ...rest } = state.uploadsInFlightByKey;
            return { uploadsInFlightByKey: rest };
          }
          return {
            uploadsInFlightByKey: {
              ...state.uploadsInFlightByKey,
              [key]: nextCount,
            },
          };
        }),
      isUploadInFlight: (key) => (get().uploadsInFlightByKey[key] ?? 0) > 0,
      hasAnyUploadInFlight: () =>
        Object.values(get().uploadsInFlightByKey).some((count) => count > 0),
      setFilesForKey: (key, files, options) =>
        set((state) => {
          const prev = state.filesByKey[key] ?? [];
          const next = resolveEntries(prev, files, options);

          if (!next.length) {
            return { filesByKey: omitKey(state.filesByKey, key) };
          }

          return {
            filesByKey: {
              ...state.filesByKey,
              [key]: next,
            },
          };
        }),
      pushFilesForKey: (key, files, options) =>
        set((state) => {
          if (!files.length) return state;
          const prev = state.filesByKey[key] ?? [];
          const next = resolveEntries(prev, files, { mode: "merge", ...options });

          if (!next.length) {
            return { filesByKey: omitKey(state.filesByKey, key) };
          }

          return {
            filesByKey: {
              ...state.filesByKey,
              [key]: next,
            },
          };
        }),
      updateFilesForKey: (key, updater) =>
        set((state) => {
          const prev = state.filesByKey[key] ?? [];
          const next = updater(prev);

          if (!next.length) {
            return { filesByKey: omitKey(state.filesByKey, key) };
          }

          return {
            filesByKey: {
              ...state.filesByKey,
              [key]: next,
            },
          };
        }),
      removeFileAtIndex: (key, index) =>
        set((state) => {
          const existing = state.filesByKey[key] ?? [];
          const next = existing.filter((_, idx) => idx !== index);

          if (!next.length) {
            return { filesByKey: omitKey(state.filesByKey, key) };
          }
          return {
            filesByKey: { ...state.filesByKey, [key]: next },
          };
        }),
      clearFilesForKey: (key) => set((state) => ({ filesByKey: omitKey(state.filesByKey, key) })),
      clearAll: () => set({ filesByKey: {}, uploadsInFlightByKey: {} }),
      getFilesForKey: (key) => get().filesByKey[key] ?? [],
    }),
    {
      name: "upload-store",
      version: 1,
      partialize: (state) => ({ filesByKey: sanitizePersistedFiles(state.filesByKey) }),
      migrate: (persistedState) => {
        const filesByKey =
          persistedState && typeof persistedState === "object"
            ? (persistedState as { filesByKey?: unknown }).filesByKey
            : undefined;

        return { filesByKey: sanitizePersistedFiles(filesByKey) };
      },
    },
  ),
);
